import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MemberAdmittedComm, MemberAdmittedMa, MemberDischargeDateMessage, MemberRepresentative, ReferralFacilityNPI, ReferralFacilityName, ReferralSource, ReferralSourceName, ReferralSourcePhone } from '@core/constants/messages';
import { PacimAuthDetailsUtilityGroup, PacimAuthorizationType } from '@core/enumeration';
import { AuthDetailsUtilityPacimService, TenantService } from '@core/service';
import { DateUtilities, SecurityService, TenantNames, noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { LEVEL_OF_PAC } from '@shared/constants';
import { Urgencies } from '@shared/enumeration';
import { AuthorizationReferenceProductDTO } from '@shared/model';
import { isUndefined, trackDateTime } from '@shared/utility';
import { amountOfCharactersValidator, phoneNumberValidator } from '@shared/validators';
import { isEqual } from 'lodash-es';
import { SelectItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, tap } from 'rxjs/operators';
import { AuthDetailsPac, AuthDetailsReferencedField } from '../../model';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'auth-details-tab-pac',
    templateUrl: './auth-details-tab-pac.component.html',
    styleUrls: ['./auth-details-tab-pac.component.scss'],
    standalone: false
})
export class AuthDetailsTabPacComponent implements OnInit {
  @Input() authorizationDetails: AuthDetailsPac | null;
  @Input() isInternalFlow!: boolean;
  @Input() productId!: string;
  @Input() tenantId!: string;
  @Input() isPacCommercial!: boolean;
  @Input() memberId: string;
  @Input() faxReceivedDateTime: string = null;
  @Input() lineOfBusinessId: string | null;

  @Output() cancelProcess = new EventEmitter<void>();
  @Output() submitStep = new EventEmitter<AuthDetailsPac>();
  @Output() autoUpdate = new EventEmitter<AuthDetailsPac>();
  @Output() updateFacilityType =  new EventEmitter<string>();
  references: Record<AuthDetailsReferencedField, SelectItem<AuthorizationReferenceProductDTO>[]>;

  form!: FormGroup;

  checkingForIncompleteAuth = false;
  currentDate = new Date();
  isNextLoading = false;
  dialogVisible = false;
  supportingPhysicianAvailable = true;
  utilitiesLoading = true;
  referralSourceMsg = ReferralSource;
  referralFacilityNameMsg = ReferralFacilityName;
  referralFacilityNPIMsg = ReferralFacilityNPI;
  referralSourceNameMsg = ReferralSourceName;
  referralSourcePhoneMsg = ReferralSourcePhone;
  memberAdmittedCommMsg = MemberAdmittedComm;
  memberAdmittedMaMsg = MemberAdmittedMa;
  memberRepresentativeMsg = MemberRepresentative;
  memberDischargeDateMsg = MemberDischargeDateMessage;
  memberPastDischargeDate = new Date();
  memberFutureDischargeDate =  new Date();

  readonly dayInMs = 86400000; // 1000 * 60 * 60 * 24;
  // admit date cannot be more than 3 days in the future;
  readonly admitDateFutureCap = new Date(new Date(this.currentDate.valueOf() + 3 * this.dayInMs).toISOString().replace('Z', ''));

  readonly memberDischargeFutureDate = new Date(this.currentDate.valueOf());
  readonly memberDischargePastdate = new Date(this.currentDate.valueOf() - 5 * this.dayInMs);

  get dischargeControls(): FormGroup | undefined {
    return this.form?.get('discharge') as FormGroup;
  }

  private allClinicalCategories: {
    [type: string]: SelectItem<AuthorizationReferenceProductDTO>[]
  };

  private urgenciesMap: {
    [Urgencies.Urgent]?: AuthorizationReferenceProductDTO;
    [Urgencies.Routine]?: AuthorizationReferenceProductDTO;
  } = {};

  constructor(
    private fb: FormBuilder,
    private authUtilities: AuthDetailsUtilityPacimService,
    private cdRef: ChangeDetectorRef,
    private securityService: SecurityService,
    private tenantService: TenantService,
    private readonly destroyRef: DestroyRef,
  ) {
  }

  ngOnInit(): void {
    this.trackCurrentDate();
    this.getDropdownOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.createForm();

        if (!this.isInternalFlow) {
          this.setUrgencyForExternal();
        }

        this.setFormAutoUpdate();
      });
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.isNextLoading = true;

      const data = this.mapFormData();
      this.submitStep.emit(data);
    }
  }

  setCheckingForIncompleteAuth(value: boolean): void {
    this.checkingForIncompleteAuth = value;
    this.cdRef.markForCheck();
  }

  onUrgencyChange(): void {
    const urgency = this.form.get('urgency').value as AuthorizationReferenceProductDTO;

    if (urgency?.description === Urgencies.Urgent) {
      this.dialogVisible = true;
    }
  }

  showRequestReceivedCalendar(): void {
    // Need to call setValue of date control on first open, for applying maxDate restriction
    let requestReceivedValue
    = new Date(this.dateWithoutMilliseconds(this.form.get('requestReceivedDate').value?.toISOString()));

    if (!DateUtilities.isDateValid(requestReceivedValue)) {
      requestReceivedValue = null;
    }

    this.form.get('requestReceivedDate').setValue(requestReceivedValue || this.currentDate);
  }

  private trackCurrentDate(): void {
    trackDateTime().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(date => {
      this.currentDate = date;
    });
  }

  private composeClinicalCategories(
    authorizationTypes: AuthorizationReferenceProductDTO[] = []
  ): { [type: string]: SelectItem<AuthorizationReferenceProductDTO>[] } {
    return authorizationTypes.reduce((categories, type) => {
      if (!this.isInternalFlow) {
        const unknownUtilityClinicalCategoryIndex = type.children.findIndex(
          category => category.description === 'Unknown'
        );

        if (unknownUtilityClinicalCategoryIndex !== -1) {
          type.children.splice(unknownUtilityClinicalCategoryIndex, 1);
        }
      }

      return {
        ...categories,
        [type.description]: type.children.map(utility => this.mapUtilityToSelectItem(utility)),
      };
    }, {});
  }

  private createForm(): void {
    this.form = this.fb.group({
      facilityType: [null, Validators.required],
      clinicalCategories: [{ disabled: true, value: null }, Validators.required],
      referralSource: [{ disabled: true, value: [] }, Validators.required],
      referralFacilityNPI: [
        { disabled: true, value: null },
        [
          Validators.required,
          amountOfCharactersValidator(10),
        ],
      ],
      unknownReferralFacilityNPI: [{ disabled: true, value: null }],
      referralFacilityName: [
        { disabled: true, value: null },
        [
          Validators.required,
          noWhiteSpaceValidator,
          Validators.maxLength(150),
        ],
      ],
      unknownReferralFacility: [{ disabled: true, value: null }],
      anticipatedAdmitDate: [{ disabled: true, value: null }, Validators.required],
      urgency: [{ disabled: true, value: null }, Validators.required],
      requestSource: [{ disabled: true, value: null }, Validators.required],
      requestor: [{ disabled: true, value: null }, Validators.required],
    });

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceContactName.Read'])) {
      const validators = [noWhiteSpaceValidator, Validators.maxLength(150)];

      if (!this.isInternalFlow) {
        validators.unshift(Validators.required);
      }

      this.form.addControl('referralSourceName', this.fb.control({ disabled: true, value: null }, validators));
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourcePhone.Read'])) {
      const validators = [phoneNumberValidator];

      if (!this.isInternalFlow) {
        validators.unshift(Validators.required);
      }

      this.form.addControl('referralSourcePhone', this.fb.control({ disabled: true, value: null }, validators));
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceFax.Read'])) {
      const validators = [phoneNumberValidator];

      if (!this.isInternalFlow) {
        validators.unshift(Validators.required);
      }

      this.form.addControl('referralSourceFax', this.fb.control({ disabled: true, value: null }, validators));
    }

    if (this.isInternalFlow) {
      this.form.addControl(
        'requestReceivedDate',
        this.faxReceivedDateTime
          ? this.fb.control({ disabled: false, value: new Date(this.faxReceivedDateTime) })
          : this.fb.control({ disabled: true, value: null }, Validators.required)
      );
    }

    this.setFieldsBehavior();

    if (this.authorizationDetails) {
      this.setDefaultFormState();
    }

    if (!this.isInternalFlow) {
      this.form.get('requestSource').setValue(
        this.references.requestSource.find(source => source.label === 'External').value
      );
      this.form.get('requestor').setValue(
        this.references.requestor.find(requestor => requestor.label === 'External').value
      );
    }
  }

  private dateWithoutMilliseconds(ISOString: string): string {
    return ISOString?.replace(/\.\d+/, '');
  }

  private getDropdownOptions(): Observable<void> {
    const lineOfBusinessId = this.lineOfBusinessId ?? this.authorizationDetails?.authorizationProduct?.lineOfBusinessId ?? null;

    return this.authUtilities.getAllUtility(this.tenantId, this.productId, lineOfBusinessId).pipe(
      finalize(() => {
        this.utilitiesLoading = false;

        this.cdRef.markForCheck();
      }),
      tap(data => {
        this.references = {
          clinicalCategories: [], // these are defined dynamically by facilityType
          facilityType: data[PacimAuthDetailsUtilityGroup.Type]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          referralSource: data[PacimAuthDetailsUtilityGroup.ReferralSource]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          urgency: data[PacimAuthDetailsUtilityGroup.Urgency]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          dischargeDisposition: data[PacimAuthDetailsUtilityGroup.DischargeDisposition]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          dischargeRationale: data[PacimAuthDetailsUtilityGroup.DischargeRationale]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          requestSource: data[PacimAuthDetailsUtilityGroup.RequestSource]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
          requestor: data[PacimAuthDetailsUtilityGroup.Requestor]
            ?.map(utility => this.mapUtilityToSelectItem(utility)) || [],
        };

        this.allClinicalCategories = this.composeClinicalCategories(data[PacimAuthDetailsUtilityGroup.Type]);

        if (this.references.dischargeRationale.length) {
          const autoDischargeRationaleIndex = this.references.dischargeRationale.findIndex(
            rationale => rationale.label === 'Auto-discharged'
          );

          if (autoDischargeRationaleIndex !== -1) {
            // Note: only show on auth details- not within the request process, see #42359
            this.references.dischargeRationale.splice(autoDischargeRationaleIndex, 1);
          }
        }

        if (this.references.urgency.length) {
          this.references.urgency = this.references.urgency.filter(urgency => {
            const description = urgency.value.description;

            return description === Urgencies.Routine || description === Urgencies.Urgent; // See #72673
          });

          this.urgenciesMap = this.references.urgency
            .reduce((result, urgency) => ({ ...result, [urgency.label]: urgency.value }), {});
        }

        if (this.isInternalFlow) {
          if (this.references.requestSource.length) {
            const externalRequestSourceIndex = this.references.requestSource.findIndex(source => source.label === 'External');

            if (externalRequestSourceIndex !== -1) {
              this.references.requestSource.splice(externalRequestSourceIndex, 1);
            }
          }

          if (this.references.requestor.length) {
            const labelsToRemove = ['External', 'PAC Facility'];
            this.references.requestor = this.references.requestor.filter(requestor => !labelsToRemove.includes(requestor.label));
          }
        }
      }),
      map(() => undefined)
    );
  }

  private mapFormData(): AuthDetailsPac {
    const result: AuthDetailsPac = {
      admitDate: DateUtilities.convertToUTCStartOfDayDate(this.form.get('anticipatedAdmitDate').value)?.toISOString(),
      urgency: this.form.get('urgency').value,
      referralFacilityName: this.form.get('referralFacilityName').value || null,
      referralFacilityNPI: this.form.get('referralFacilityNPI').value || null,
      referralSource: this.form.get('referralSource').value,
      clinicalCategories: [this.form.get('clinicalCategories').value],
      facilityType: this.form.get('facilityType').value,
      requestSource: this.form.get('requestSource').value,
      requestor: this.form.get('requestor').value,
      authorizationProduct: { memberId: this.memberId,
        requestReceivedDate: new Date(this.dateWithoutMilliseconds(this.form.get('requestReceivedDate')?.value?.toISOString())),
        submittedBy: null},
      transferType: this.authorizationDetails?.transferType || null,
      hasPreviousCarrierDocuments: this.authorizationDetails?.hasPreviousCarrierDocuments || null,
    };

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceContactName.Read'])) {
      result.referralSourceContactName = this.form.get('referralSourceName').value;
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourcePhone.Read'])) {
      result.referralSourcePhone = this.form.get('referralSourcePhone').value;
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceFax.Read'])) {
      result.referralSourceFax = this.form.get('referralSourceFax').value;
    }

    const discharge = this.form.get('discharge');

    if (discharge) {
      result.dischargeDisposition = discharge.get('dischargeDisposition').value;
      result.dischargeRationale = discharge.get('dischargeRationale').value;
      result.memberDischargeDate = DateUtilities.convertToUTCStartOfDayDate(
        discharge.get('memberDischargeDate').value
      ).toISOString();
    } else {
      result.dischargeDisposition = null;
      result.dischargeRationale = null;
      result.memberDischargeDate = null;
    }

    const memberAdmitted = this.form.get('memberAdmitted')?.value as boolean;

    if (!isUndefined(memberAdmitted)) {
      result.memberCurrentlyAdmitted = memberAdmitted;
    }

    if (this.isInternalFlow) {
      result.authorizationProduct.requestReceivedDate
        = new Date(this.dateWithoutMilliseconds(this.form.get('requestReceivedDate').value.toISOString()));

      const memberRepresentative = this.form.get('memberRepresentative')?.value as string || null;

      result.memberRepresentative = memberRepresentative;
    }

    return result;
  }

  private mapUtilityToSelectItem(utility: AuthorizationReferenceProductDTO): SelectItem<AuthorizationReferenceProductDTO> {
    return {
      value: { ...utility, children: [] },
      label: utility.description,
    };
  }

  onAnticipatedDateChange(): void {
    this.memberAdmittedBehavior();
    this.dischargeDateBehavior();
    this.urgencyBehavior();
  }

  onMemberAdmittedChange(): void {
    this.dischargeDateBehavior();
    this.urgencyBehavior();
  }

  private dischargeDateBehavior(): void {
    const isAdmitted = this.form.get('memberAdmitted')?.value;
    const anticipatedDate = this.form.get('anticipatedAdmitDate').value;

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    if (this.isInternalFlow === false) {
      this.memberPastDischargeDate = this.memberDischargePastdate;
      this.memberFutureDischargeDate = this.memberDischargeFutureDate;
    } else {
      this.memberPastDischargeDate = this.form.get('anticipatedAdmitDate').value;
      this.memberFutureDischargeDate = null;
    }

    if (isAdmitted === false && anticipatedDate.getTime() < currentDate.getTime()) {
      if (!this.form.get('discharge')) {
        this.form.addControl('discharge', this.fb.group({
          memberDischargeDate: [null, Validators.required],
          dischargeDisposition: [null, Validators.required],
          dischargeRationale: [null, Validators.required],
        }));
      } else {
        this.form.get('discharge').get('memberDischargeDate').setValue(null);
      }
    } else {
      this.form.removeControl('discharge');
    }
  }

  private urgencyBehavior(): void {
    const isAdmitted = this.form.get('memberAdmitted')?.value as boolean;
    const anticipatedDate = this.form.get('anticipatedAdmitDate').value as Date;
    const urgencyControl = this.form.get('urgency');

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const isDateEqual = anticipatedDate.getTime() === currentDate.getTime();

    if (anticipatedDate.getTime() < currentDate.getTime()) {
      urgencyControl.setValue(this.urgenciesMap[Urgencies.Routine]);
      urgencyControl.disable();
    } else if (isDateEqual) {
      urgencyControl.setValue(this.urgenciesMap[Urgencies.Routine]);

      if (this.isInternalFlow && !isAdmitted) {
        urgencyControl.enable();
      } else {
        urgencyControl.disable();
      }
    } else if (this.isInternalFlow) {
      urgencyControl.enable();
    }
  }

  private memberAdmittedBehavior(): void {
    if (this.form.get('anticipatedAdmitDate')?.value) {
      const anticipatedDate = this.form.get('anticipatedAdmitDate').value;

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const dateIsPast = anticipatedDate.getTime() <= currentDate.getTime();

      if (dateIsPast) {
        this.form.addControl('memberAdmitted', this.fb.control(null, Validators.required));
        this.form.get('memberAdmitted').markAsDirty();
      } else {
        this.form.removeControl('memberAdmitted');
      }
    } else {
      this.form.removeControl('memberAdmitted');
    }
  }

  private setDefaultFormState(): void {
    this.form.get('facilityType').setValue(this.authorizationDetails.facilityType);
    this.form.get('clinicalCategories').setValue(this.authorizationDetails.clinicalCategories?.[0]);
    this.form.get('referralSource').setValue(this.authorizationDetails.referralSource);
    this.form.get('referralFacilityNPI').setValue(this.authorizationDetails.referralFacilityNPI);
    this.form.get('referralFacilityName').setValue(this.authorizationDetails.referralFacilityName);

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceContactName.Read'])) {
      this.form.get('referralSourceName').setValue(this.authorizationDetails.referralSourceContactName);
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourcePhone.Read'])) {
      this.form.get('referralSourcePhone').setValue(this.authorizationDetails.referralSourcePhone);
    }

    if (this.securityService.hasAccess(['PACIMAuthorization.ReferralSourceFax.Read'])) {
      this.form.get('referralSourceFax').setValue(this.authorizationDetails.referralSourceFax);
    }

    if (this.authorizationDetails.referralFacilityNPI === null) {
      this.form.get('unknownReferralFacilityNPI').setValue(true);
    }

    if (this.authorizationDetails.referralFacilityName === null) {
      this.form.get('unknownReferralFacility').setValue(true);
    }

    this.form.get('urgency').setValue(this.authorizationDetails.urgency);

    const anticipatedDate = new Date(this.authorizationDetails.admitDate?.replace('Z', ''));
    anticipatedDate.setHours(0, 0, 0, 0);
    const anticipatedDateTime = anticipatedDate.getTime();

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const currentDateTime = currentDate.getTime();

    this.form.get('anticipatedAdmitDate').setValue(anticipatedDate);
    this.memberAdmittedBehavior();

    if (anticipatedDateTime <= currentDateTime) {
      const memberAdmitted = this.authorizationDetails.memberCurrentlyAdmitted;

      this.form.get('memberAdmitted').setValue(memberAdmitted);
      this.dischargeDateBehavior();

      if (anticipatedDateTime === currentDateTime && !(this.isInternalFlow && !memberAdmitted)) {
        this.form.get('urgency').disable();
      }

      if (anticipatedDateTime < currentDateTime) {
        this.form.get('urgency').disable();

        if (!memberAdmitted) {
          this.form.get('discharge').setValue({
            memberDischargeDate: new Date(this.authorizationDetails.memberDischargeDate.replace('Z', '')),
            dischargeDisposition: this.authorizationDetails.dischargeDisposition,
            dischargeRationale: this.authorizationDetails.dischargeRationale,
          });
        }
      }
    }

    this.form.get('requestSource').setValue(this.authorizationDetails.requestSource);
    this.form.get('requestor').setValue(this.authorizationDetails.requestor);

    if (this.isInternalFlow) {
      if (!this.faxReceivedDateTime && this.authorizationDetails.authorizationProduct?.requestReceivedDate) {
        this.form.get('requestReceivedDate').setValue(new Date(this.authorizationDetails.authorizationProduct?.requestReceivedDate));
      }
      if (this.authorizationDetails.memberRepresentative) {
        this.form.get('memberRepresentative').setValue(this.authorizationDetails.memberRepresentative);
      }
    }
  }

  private setFieldsBehavior(): void {
    this.setTypeBehavior();
    this.setReferralFacilityNPIBehavior();
    this.setReferralFacilityNameBehavior();
    this.setClinicalCategoriesBehavior();
    this.setRequestorBehavior();
  }

  private setClinicalCategoriesBehavior(): void {
    const controlsToSkip: AuthDetailsReferencedField[] = ['clinicalCategories', 'facilityType'];

    if (!this.isInternalFlow) {
      // These controls are set automatically for External User, so don't touch them here
      controlsToSkip.push('urgency', 'requestSource', 'requestor');
    }

    const clinicalCategoriesControl = this.form.get('clinicalCategories');

    clinicalCategoriesControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories: AuthorizationReferenceProductDTO) => {
        const formData = {};

        Object.keys(this.form.controls).filter(controlName => {
          if (this.faxReceivedDateTime && controlName === 'requestReceivedDate') {
            return false;
          }

          return !controlsToSkip.includes(controlName as AuthDetailsReferencedField);
        }).forEach(controlName => {
          formData[controlName] = null;

          const control = this.form.get(controlName);

          if (categories) {
            // Note: this may seem a bit redundant, but it's meant to prevent bugs related to behavior of other fields
            if (control.disabled) {
              control.enable();
            }
          } else {
            control.disable();
          }
        });

        this.form.patchValue(formData);
        this.form.removeControl('memberAdmitted');
        this.form.removeControl('discharge');
      });
  }

  private setFormAutoUpdate(): void {
    this.form.valueChanges.pipe(
      debounceTime(500),
      filter(() => this.form.valid),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      map(() => this.mapFormData()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(submitData => {
      this.autoUpdate.emit(submitData);
    });
  }

  private setReferralFacilityNameBehavior(): void {
    const referralFacilityNameControl = this.form.get('referralFacilityName');
    const unknownReferralFacilityNameControl = this.form.get('unknownReferralFacility');
    const clinicalCategoriesControl = this.form.get('clinicalCategories');

    unknownReferralFacilityNameControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((facilityUnknown: boolean) => {
        // eslint-disable-next-line max-lines
        if (facilityUnknown) {
          referralFacilityNameControl.disable();
          // Form marks itself as untouched if its only touched control was disabled,
          // but it's a confusing behavior, so we have to convince it otherwise

          if (this.form.untouched) {
            this.form.markAsTouched();
          }
        } else if (referralFacilityNameControl.disabled && clinicalCategoriesControl.value) {
          referralFacilityNameControl.enable();
        }
      });

    referralFacilityNameControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((facilityName: string) => {
        if (facilityName?.length) {
          unknownReferralFacilityNameControl.disable();
        } else if (unknownReferralFacilityNameControl.disabled && clinicalCategoriesControl.value) {
          unknownReferralFacilityNameControl.enable();
        }
      });
  }

  private setReferralFacilityNPIBehavior(): void {
    const referralFacilityNPIControl = this.form.get('referralFacilityNPI');
    const unknownFacilityNPIControl = this.form.get('unknownReferralFacilityNPI');
    const clinicalCategoriesControl = this.form.get('clinicalCategories');

    unknownFacilityNPIControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((npiUnknown: boolean) => {
        if (npiUnknown) {
          referralFacilityNPIControl.disable();
          // Form marks itself as untouched if its only touched control was disabled,
          // but it's a confusing behavior, so we have to convince it otherwise

          if (this.form.untouched) {
            this.form.markAsTouched();
          }
        } else if (referralFacilityNPIControl.disabled && clinicalCategoriesControl.value) {
          referralFacilityNPIControl.enable();
        }
      });

    referralFacilityNPIControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((facilityNpi: string) => {
        if (facilityNpi?.length) {
          unknownFacilityNPIControl.disable();
        } else if (unknownFacilityNPIControl.disabled && clinicalCategoriesControl.value) {
          unknownFacilityNPIControl.enable();
        }
      });
  }

  private setRequestorBehavior(): void {
    this.form.get('requestor').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((requestor: AuthorizationReferenceProductDTO | null) => {
        if (requestor?.description === 'Member Representative') {
          this.form.addControl('memberRepresentative', this.fb.control(null, [
            Validators.required,
            noWhiteSpaceValidator,
            Validators.maxLength(100),
          ]));
        } else if (this.form.get('memberRepresentative')) {
          this.form.removeControl('memberRepresentative');
        }
      });
  }

  private setTypeBehavior(): void {
    const clinicalCategoriesControl = this.form.get('clinicalCategories');

    this.form.get('facilityType').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: AuthorizationReferenceProductDTO) => {
        clinicalCategoriesControl.setValue(null);

        const tenantName = this.tenantService.getTenantById(this.tenantId)?.name;
        const isAnthemTenant = tenantName === TenantNames.Anthem;
        const isWellpointTenant = tenantName === TenantNames.Wellpoint;
        const isEligibleTenant = isAnthemTenant || (this.isPacCommercial && isWellpointTenant);

        if (type) {
          this.updateFacilityType.emit(type.description);

          if (type.description === PacimAuthorizationType.SNF && (!isEligibleTenant || !this.isInternalFlow)) {
            this.references.clinicalCategories = this.allClinicalCategories[type.description]
              .filter(item => item.value.description !== LEVEL_OF_PAC);
          } else {
            this.references.clinicalCategories = this.allClinicalCategories[type.description];
          }
        }

        if (clinicalCategoriesControl.disabled) {
          clinicalCategoriesControl.enable({ emitEvent: false });
        }
      });
  }

  private setUrgencyForExternal(): void {
    const urgencyControl = this.form.get('urgency');

    urgencyControl.setValue(this.urgenciesMap[Urgencies.Routine]);
    urgencyControl.disable();
  }
}
